import React, { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, PhoneCall, X, Tag, NotePencil } from "@phosphor-icons/react";
import API from "../services/api";
import "./History.css";

const TAG_OPTIONS = ["Hot Lead", "Follow Up", "Not Interested", "Booked", "Voicemail", "Callback"];

export default function History() {
  const dialerState = window.__ds || {};
  const [callerId, setCallerId] = useState("");

  useEffect(() => {
    API.get("/twilio/credentials").then(({ data }) => {
      setCallerId(data.caller_id || "");
    }).catch(() => {});
  }, []);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = filter !== "all" ? `?direction=${filter}` : "";
    API.get(`/calls${params}`).then(({ data }) => setCalls(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  const handleSaveNote = async () => {
    if (!selected || !note.trim()) return;
    setSaving(true);
    try {
      await API.patch(`/calls/${selected.id}`, { notes: note });
      setCalls(cs => cs.map(c => c.id === selected.id ? { ...c, notes: note } : c));
      setSelected(s => ({ ...s, notes: note }));
    } catch {}
    setSaving(false);
  };

  const handleTagToggle = async (tag) => {
    if (!selected) return;
    const current = selected.tags || [];
    const updated = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    try {
      await API.patch(`/calls/${selected.id}`, { tags: updated });
      setCalls(cs => cs.map(c => c.id === selected.id ? { ...c, tags: updated } : c));
      setSelected(s => ({ ...s, tags: updated }));
    } catch {}
  };

  const formatDuration = (s) => { if (!s) return "—"; const m = Math.floor(s / 60); return m > 0 ? `${m}m ${s % 60}s` : `${s}s`; };
  const formatDate = (d) => new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const getDisplayNumber = (call) => {
    if (call.first_name) return `${call.first_name} ${call.last_name}`;
    if (call.direction === "inbound") return call.from_number || "Unknown";
    return call.to_number || call.from_number || "Unknown";
  };
  const getStatusClass = (s) => s === "completed" ? "badge-teal" : ["no-answer","busy","failed"].includes(s) ? "badge-danger" : "badge-gray";

  return (
    <div className="history-page">
      <div className="page-header">
        <div><h1 className="page-title">Call History</h1><p className="page-subtitle">{calls.length} calls</p></div>
        <div className="header-actions">
          {["all","inbound","outbound"].map(f => (
            <button key={f} className={`btn btn-sm ${filter === f ? "btn-primary" : "btn-secondary"}`} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : f === "inbound" ? "Inbound" : "Outbound"}
            </button>
          ))}
        </div>
      </div>
      <div className="history-layout">
        <div className="history-list">
          {loading && <div className="spinner-container"><span className="spinner"/></div>}
          {!loading && calls.length === 0 && <div className="empty-state"><p>No calls yet.</p></div>}
          {calls.map(call => (
            <div key={call.id} className={`history-item ${selected?.id === call.id ? "active" : ""}`}
              onClick={() => { setSelected(call); setNote(call.notes || ""); }}>
              <div className={`direction-icon ${call.direction}`}>
                {call.direction === "inbound" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
              </div>
              <div className="history-info">
                <div className="history-number">{getDisplayNumber(call)}</div>
                <div className="history-meta">{formatDate(call.created_at)} · {formatDuration(call.duration)}</div>
              </div>
              <div className="history-right">
                <span className={`badge ${getStatusClass(call.status)}`}>{call.status}</span>
                {call.tags?.length > 0 && <span className="badge badge-purple">{call.tags[0]}</span>}
              </div>
            </div>
          ))}
        </div>
        {selected && (
          <div className="history-detail">
            <div className="detail-header">
              <div><h2>{getDisplayNumber(selected)}</h2><p>{selected.direction} · {formatDate(selected.created_at)} · {formatDuration(selected.duration)}</p></div>
              <div className="detail-header-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => { const num = selected.direction === "inbound" ? selected.from_number : selected.to_number; dialerState.dial?.(num, callerId); }}>
                  <PhoneCall size={13} /> Call back
                </button>
                <button className="btn-icon" onClick={() => setSelected(null)}><X size={16} /></button>
              </div>
            </div>
            <div className="detail-body">
              <div className="detail-section">
                <span className="label" style={{ display:"flex", alignItems:"center", gap:5 }}><Tag size={11} /> Tags</span>
                <div className="tags-grid">
                  {TAG_OPTIONS.map(tag => (
                    <button key={tag} className={`tag-btn ${(selected.tags || []).includes(tag) ? "active" : ""}`} onClick={() => handleTagToggle(tag)}>{tag}</button>
                  ))}
                </div>
              </div>
              <div className="detail-section">
                <span className="label" style={{ display:"flex", alignItems:"center", gap:5 }}><NotePencil size={11} /> Notes</span>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Add call notes..." rows={5} style={{ resize:"vertical" }} />
                <button className="btn btn-primary btn-sm" style={{ marginTop:8 }} onClick={handleSaveNote} disabled={saving || !note.trim()}>
                  {saving ? "Saving..." : "Save Notes"}
                </button>
              </div>
              <div className="detail-section">
                <span className="label">Details</span>
                <div className="detail-row"><span>From</span><span className="font-mono">{selected.direction === "inbound" ? selected.from_number : selected.to_number}</span></div>
                <div className="detail-row"><span>My Number</span><span className="font-mono">{selected.direction === "inbound" ? selected.to_number : selected.from_number}</span></div>
                <div className="detail-row"><span>Status</span><span className={`badge ${getStatusClass(selected.status)}`}>{selected.status}</span></div>
                <div className="detail-row"><span>Duration</span><span>{formatDuration(selected.duration)}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
